import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import CountUp from "react-countup";
import { getSellerStatsQuery } from "../../queries";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import getChartColorsArray from "../Common/ChartsDynamicColor";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/uz";
import "dayjs/locale/uz-latn";
import { useTranslation } from "react-i18next";

const SellerStatsDiagram = () => {
  const { sellerId } = useParams();
  const { t, i18n } = useTranslation();

  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalPaymentAmount: 0,
    totalTransactions: 0,
    totalAmountTransactions: 0,
  });

  const linechartcustomerColors = getChartColorsArray(
    '["--vz-primary", "--vz-success", "--vz-danger"]'
  );

  const { data: stats } = useQuery({
    ...getSellerStatsQuery(sellerId),
  });

  const statsData = stats?.data?.data || [];

  const options = {
    chart: {
      height: 370,
      type: "line",
      toolbar: {
        show: false,
      },
    },
    stroke: {
      width: [2, 2, 2],
    },
    xaxis: {
      categories: chartData?.map((entry) => {
        const dayjsLang = i18n.language === "uz" ? "uz-latn" : i18n.language === "uzc" ? "uz" : i18n.language === "en" ? "en" : "ru";
        return dayjs(entry.date).locale(dayjsLang).format("MMM, YY");
      }),
    },
    colors: linechartcustomerColors,
    tooltip: {
      shared: true,
    },
  };

  useEffect(() => {
    if (statsData) {
      setChartData(statsData);
      const total = statsData.reduce(
        (acc, curr) => {
          acc.totalPayments += curr.totalPayments;
          acc.totalPaymentAmount += curr.totalAmountPayment;
          acc.totalTransactions += curr.totalTransactions;
          acc.totalAmountTransactions += curr.totalAmountTransactions;
          return acc;
        },
        {
          totalPayments: 0,
          totalPaymentAmount: 0,
          totalTransactions: 0,
          totalAmountTransactions: 0,
        }
      );
      setSummary(total);
    }
  }, [statsData]);

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">{t("Revenue")}</h4>
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp
                    start={0}
                    end={summary.totalPayments}
                    duration={3}
                    separator=","
                  />
                </h5>
                <p className="text-muted mb-0">{t("Total Payments")}</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp
                    start={0}
                    end={summary.totalPaymentAmount}
                    duration={3}
                  />
                </h5>
                <p className="text-muted mb-0">{t("Total Payment Amount")}</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp
                    start={0}
                    end={summary.totalTransactions}
                    duration={3}
                  />
                </h5>
                <p className="text-muted mb-0">{t("Total Transactions")}</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0 border-end-0">
                <h5 className="mb-1 text-success">
                  <CountUp
                    start={0}
                    end={summary.totalAmountTransactions}
                    duration={3}
                  />
                </h5>
                <p className="text-muted mb-0">
                  {t("Total Amount Transactions")}
                </p>
              </div>
            </Col>
          </Row>
        </CardHeader>

        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              <ReactApexChart
                dir="ltr"
                options={options}
                series={[
                  {
                    name: t("Payments"),
                    data: chartData.map((entry) => entry.totalPayments),
                  },
                  {
                    name: t("Total Payment Amount"),
                    data: chartData.map((entry) => entry.totalAmountPayment),
                  },
                  {
                    name: t("Transactions"),
                    data: chartData.map((entry) => entry.totalTransactions),
                  },
                  {
                    name: t("Transactions Amount"),
                    data: chartData.map(
                      (entry) => entry.totalAmountTransactions
                    ),
                  },
                ]}
                type="area"
                height="370"
                className="apex-charts"
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default SellerStatsDiagram;
